import React from "react";
import styles from "./skeleton.module.scss";
import classNames from "classnames";
import { Grid } from "@mui/material";

type Props = {
  width?: string;
  height?: string;
  borderRadius?: string;
  withTitle?: boolean;
  withDescription?: boolean;
  /** Amount of description lines */
  descriptionCount?: number;
  className?: string;
  variant?: "block" | "list" | "grid" | "form";
  /** Amount of blocks for listed and grid skeleton */
  amountOfBlocks?: number;
  /** XS property for MUI Grid item */
  itemXs?: number;
  /** Spacing for listed and grid */
  spacing?: number;
  mt?: number;
};

const SkeletonLoader: React.FC<Props> = ({
  width = "100%",
  height = "230px",
  borderRadius = "8px",
  withTitle = false,
  withDescription = false,
  descriptionCount = 2,
  className,
  variant = "block",
  amountOfBlocks = 6,
  itemXs = 4,
  spacing = 1.5,
  mt,
}) => {
  const SkeletonBlock = () => (
    <div
      className={classNames(styles.skeletonContainer, className)}
      style={{ marginTop: variant === "block" || !variant ? mt : 0 }}
    >
      {withTitle && <div className={styles.skeletonTitle}></div>}
      {withDescription &&
        [...Array(descriptionCount)].map((_item, idx) => {
          const randomCoefficient = Math.random() * (1 - 0.6) + 0.6;

          return (
            <div
              style={{
                width: `${100 * randomCoefficient}%`,
              }}
              className={styles.skeletonDescription}
              key={idx}
            />
          );
        })}
      <div
        className={classNames(styles.skeleton, styles.skeletonBlock)}
        style={{ width, height, borderRadius }}
      />
    </div>
  );

  switch (variant) {
    case "grid":
      return (
        <Grid padding={0} container spacing={spacing} style={{ marginTop: mt }}>
          {[...Array(amountOfBlocks)].map((_item, idx) => (
            <Grid padding={0} item xs={itemXs} key={idx}>
              <SkeletonBlock />
            </Grid>
          ))}
        </Grid>
      );
    case "list":
      return (
        <div
          className={styles.list}
          style={{
            gap: `${spacing * 4}px`,
            marginTop: mt,
          }}
        >
          {[...Array(amountOfBlocks)].map((_item, idx) => (
            <SkeletonBlock key={idx} />
          ))}
        </div>
      );
    case "block":
    default:
      return <SkeletonBlock />;
  }
};

export default SkeletonLoader;
